import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import fileDownload from 'js-file-download';
import {
  confirmEvidenceDownloadCompletionInAPI,
  FinalXmlArchiveRetrievalResult,
  getFinalXmlArchiveFromAPI,
  isSupplementaryDocumentIntegrityLinkFinalXmlError,
} from '../services/eCMRService';

const FinalXmlArchive: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const documentId = id || '';
  const [archive, setArchive] = useState<FinalXmlArchiveRetrievalResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [errorCode, setErrorCode] = useState<'supplementary_integrity_required' | ''>('');
  const [downloadCompletionStatus, setDownloadCompletionStatus] = useState<'confirmed' | 'failed' | ''>('');

  useEffect(() => {
    if (!documentId) {
      setError('Missing API document id.');
      setLoading(false);
      return;
    }

    const loadArchive = async () => {
      setLoading(true);
      setError('');
      setErrorCode('');
      try {
        setArchive(await getFinalXmlArchiveFromAPI(documentId));
      } catch (loadError) {
        if (isSupplementaryDocumentIntegrityLinkFinalXmlError(loadError)) {
          setErrorCode('supplementary_integrity_required');
        }
        setError('No se pudo recuperar el XML final archivado. Inténtalo de nuevo más tarde.');
      } finally {
        setLoading(false);
      }
    };

    loadArchive();
  }, [documentId]);

  const downloadFinalXml = async () => {
    if (!archive) {
      return;
    }
    setDownloadCompletionStatus('');
    const safeDocumentId = documentId.replace(/[^a-zA-Z0-9_-]/g, '_') || 'document';
    fileDownload(archive.xmlContent, `ecmr_${safeDocumentId}_final_archived.xml`);

    const evidenceType = archive.archiveReference?.evidenceType;
    if (evidenceType === 'final_xml') {
      try {
        await confirmEvidenceDownloadCompletionInAPI(documentId, { evidenceType });
        setDownloadCompletionStatus('confirmed');
      } catch (_completionError) {
        setDownloadCompletionStatus('failed');
      }
    }
  };

  const reference = archive?.archiveReference;

  return (
    <div className="container mx-auto p-4">
      <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold">XML final archivado</h1>
          <p className="text-sm text-gray-600">Recuperación API con verificación de integridad backend para documento {documentId || 'desconocido'}.</p>
        </div>
        <Link to="/" className="text-blue-600 hover:text-blue-900 underline">Volver a documentos</Link>
      </div>

      <div className="mb-4 rounded border-l-4 border-blue-500 bg-blue-50 p-4 text-blue-800">
        Baseline técnico parcial: esta vista descarga el XML final archivado desde el sistema de registro tras verificación backend. No prueba la descarga extremo a extremo del cliente ni sustituye una retención WORM completa.
      </div>

      {loading && <p>Recuperando XML final archivado...</p>}
      {error && errorCode !== 'supplementary_integrity_required' && (
        <p className="mb-4 rounded border-l-4 border-red-500 bg-red-100 p-4 text-red-700">{error}</p>
      )}
      {errorCode === 'supplementary_integrity_required' && (
        <p className="mb-4 rounded border-l-4 border-red-500 bg-red-100 p-4 text-red-700">
          XML final archivado bloqueado por documentos suplementarios sin enlace de integridad vigente. Revise los adjuntos para confirmar SHA-256, almacenamiento backend y version actual antes de recuperar el XML final.{' '}
          <Link to={`/attachments/${documentId}`} className="font-medium underline">Gestionar adjuntos</Link>
        </p>
      )}

      {!loading && !error && archive && (
        <div className="space-y-4">
          <section className="rounded bg-white p-4 shadow-md">
            <h2 className="mb-3 text-lg font-semibold">Referencia de archivo sanitizada</h2>
            <dl className="grid gap-2 sm:grid-cols-2">
              <div><dt className="font-medium">Evidencia</dt><dd>{reference?.evidenceReferencePresent ? 'Evidencia disponible' : 'No disponible'}</dd></div>
              <div><dt className="font-medium">Tipo</dt><dd>{reference?.evidenceType === 'final_xml' ? 'XML final archivado' : 'No disponible'}</dd></div>
              <div><dt className="font-medium">Versión</dt><dd>{reference?.documentVersionReferencePresent || reference?.documentVersionNumber !== undefined ? 'Versión disponible' : 'No disponible'}</dd></div>
              <div><dt className="font-medium">Digest</dt><dd>{reference?.digestAlgorithm ? 'Digest disponible' : 'No disponible'}</dd></div>
              <div><dt className="font-medium">Retención</dt><dd>{reference?.retentionMode ? 'Retención configurada' : 'No disponible'}</dd></div>
              <div><dt className="font-medium">Retención</dt><dd>{reference?.retainUntilPresent ? 'Fecha de retención disponible' : 'No disponible'}</dd></div>
            </dl>
          </section>

          <section className="rounded bg-white p-4 shadow-md">
            <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <h2 className="text-lg font-semibold">Contenido XML recuperado</h2>
              <button
                type="button"
                onClick={downloadFinalXml}
                className="rounded bg-blue-600 px-4 py-2 font-bold text-white hover:bg-blue-700"
              >
                Descargar XML final
              </button>
            </div>
            {downloadCompletionStatus === 'confirmed' && (
              <p className="mb-3 rounded border-l-4 border-green-500 bg-green-50 p-3 text-sm text-green-800">
                Descarga confirmada como evidencia de baseline técnico parcial.
              </p>
            )}
            {downloadCompletionStatus === 'failed' && (
              <p className="mb-3 rounded border-l-4 border-yellow-500 bg-yellow-50 p-3 text-sm text-yellow-800">
                Descarga realizada, pero no se pudo confirmar la evidencia de finalización; el XML descargado no se bloqueó.
              </p>
            )}
            <pre className="max-h-96 overflow-auto rounded bg-gray-900 p-4 text-sm text-gray-100 whitespace-pre-wrap">
              {archive.xmlContent}
            </pre>
          </section>
        </div>
      )}
    </div>
  );
};

export default FinalXmlArchive;
